import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  Check,
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { useAppSelector } from '../store';
import { getEmployeeFetchTime, getFrontendCacheUUID } from '../services/employeeDB';

function formatFetchTime(ts: number): string {
  if (ts === 0) return 'Never';
  return `${new Date(ts).toUTCString()} (${ts})`;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5 }}>
        {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

export default function SystemInfo() {
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);
  const [allCopied, setAllCopied] = useState(false);

  const dbKey = selectedUnit
    ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
    : 'cranberry-employees';

  const fetchTime = getEmployeeFetchTime(dbKey);
  const fetchTimeDisplay = formatFetchTime(fetchTime);
  const cacheId = getFrontendCacheUUID(dbKey) ?? '';

  const handleCopyAll = useCallback(() => {
    const payload = JSON.stringify(
      { fetchTime: fetchTimeDisplay, cacheId },
      null,
      2
    );
    navigator.clipboard.writeText(payload).then(() => {
      setAllCopied(true);
      setTimeout(() => setAllCopied(false), 1500);
    });
  }, [fetchTimeDisplay, cacheId]);

  return (
    <DashboardLayout>
      <Box sx={{ p: 3, maxWidth: 700 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            System Info
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={allCopied ? <Check color="success" /> : <ContentCopy />}
            onClick={handleCopyAll}
            color={allCopied ? 'success' : 'primary'}
          >
            {allCopied ? 'Copied!' : 'Copy all as JSON'}
          </Button>
        </Box>
        <Paper variant="outlined">
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '40%' }}>Fetch time (UTC)</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {fetchTimeDisplay}
                    <CopyButton value={fetchTimeDisplay} />
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cache Id</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace' }}>{cacheId || '—'}</span>
                    {cacheId && <CopyButton value={cacheId} />}
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </DashboardLayout>
  );
}
